import { shallow } from "zustand/shallow";

import cx from "@hsl/core/utils/cx";
import { shareClassGuard } from "@hsl/fund-explorer/metaSchemas";
import type {
    AllShareClassMeta,
    CollectivesShareClassMeta,
    ETFShareClassMeta,
    PMCShareClassMeta,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import useGroupedShareClasses from "../../../hooks/useGroupedShareClasses";
import { Translate } from "../../../hooks/useTranslation";
import kidOrKiid from "../../../lib/kidOrKiid";
import useExplorerStore from "../../../store/store";
import Collapsible from "../../Collapsible";
import PdfIcon from "../../PdfIcon";
import { useDocumentContext } from "../context";
import {
    FactSheetOrKiid,
    type Props as FactSheetOrKiidProps,
} from "../FactSheetOrKiid";

interface Props {
    type: "factSheet" | "kiid" | "memberCommunication";
    shareClasses: string[];
    rowClassName?: string;
}

const FactSheetOrKIIDModalTabContent = ({
    type,
    shareClasses,
    rowClassName,
}: Props) => {
    const { jurisdictionId, productType, getShareClassById } = useExplorerStore(
        ({ config: { jurisdictionId, productType }, getShareClassById }) => ({
            jurisdictionId,
            productType,
            getShareClassById,
        }),
        shallow,
    );
    const { groupOn, wrappedFund, selectionActive } = useDocumentContext();
    const customGrouping = wrappedFund && Boolean(groupOn);
    const groupedShareClasses = useGroupedShareClasses(
        shareClasses,
        customGrouping ? (groupOn as string) : undefined,
        false,
        productType === "pmc" ? ["primary"] : [],
    );
    const filteredGroupedShareClasses = Object.fromEntries(
        Object.keys(groupedShareClasses)
            .map((groupKey) => [
                groupKey,
                groupedShareClasses[groupKey]?.filter((shareClass) => {
                    const DATA = shareClass.data.data as AllShareClassMeta;
                    if (type === "factSheet") {
                        return Array.isArray(DATA.factSheet);
                    } else if (type === "kiid") {
                        return (
                            Array.isArray(DATA.kid) || Array.isArray(DATA.kiid)
                        );
                    } else if (type === "memberCommunication") {
                        return Array.isArray(DATA.memberCommunication);
                    }
                }),
            ])
            .filter((group) => Array.isArray(group[1]) && group[1].length > 0),
    );
    const showGrouped = productType !== "pmc" || wrappedFund;

    return (
        <>
            {showGrouped
                ? Object.keys(filteredGroupedShareClasses).map(
                      (shareClassGroup, i) => {
                          const groupData =
                              filteredGroupedShareClasses[shareClassGroup];

                          if (!groupData || groupData?.length === 0) {
                              return null;
                          }
                          return (
                              <Collapsible
                                  key={shareClassGroup}
                                  label={
                                      <span>
                                          {!customGrouping ? (
                                              <Translate
                                                  insert={shareClassGroup}
                                                  children={"{} share classes"}
                                              />
                                          ) : (
                                              shareClassGroup
                                          )}
                                      </span>
                                  }
                                  labelClassNames="w-full sm:border-b border-platinum py-2 justify-end flex-row-reverse text-dark-blue"
                                  withChevron
                                  isOpen={i === 0}
                              >
                                  <div
                                      className={cx(
                                          "border-platinum -mt-2 w-full py-1 sm:mt-0 sm:border-b",
                                          rowClassName,
                                      )}
                                  >
                                      {groupData.map(renderFactSheet)}
                                  </div>
                              </Collapsible>
                          );
                      },
                  )
                : shareClasses
                      .map(getShareClassById)
                      .filter(shareClassGuard)
                      .map(renderFactSheet)}
        </>
    );

    function renderFactSheet(shareClass: ShareClassDataWithType) {
        const { factSheet } = shareClass.data.data;
        const { shortName } = shareClass.data.data as
            | CollectivesShareClassMeta
            | ETFShareClassMeta;
        const { iterationDetail, iterationPriceSeries, memberCommunication } =
            shareClass.data.data as PMCShareClassMeta;
        const isPMC = productType === "pmc";
        const displayName = isPMC
            ? wrappedFund
                ? iterationPriceSeries
                : iterationDetail
            : shortName;

        if (type === "kiid") {
            const keyDoc = kidOrKiid(
                jurisdictionId,
                shareClass.data.data as AllShareClassMeta,
                productType,
            );
            return (
                <ModalFactSheetOrKIID
                    key={`${displayName} KIID`}
                    factSheetOrKiid={keyDoc.value}
                    fileName={`${displayName} ${keyDoc.type.toUpperCase()}`}
                    selectionActive={selectionActive}
                    label={keyDoc.type.toUpperCase()}
                    nullReplacementElem={<PdfIcon className="opacity-50" />}
                    displayName={displayName}
                    type={type}
                />
            );
        }
        return (
            <ModalFactSheetOrKIID
                key={`${displayName} Fact sheet`}
                factSheetOrKiid={
                    type === "memberCommunication"
                        ? memberCommunication
                        : factSheet
                }
                fileName={`${displayName} Fact Sheet`}
                selectionActive={selectionActive}
                label="Fact sheet"
                nullReplacementElem={<PdfIcon className="opacity-50" />}
                displayName={displayName}
                type={type}
            />
        );
    }
};

export default FactSheetOrKIIDModalTabContent;

const ModalFactSheetOrKIID = ({
    factSheetOrKiid,
    fileName,
    selectionActive,
    label,
    displayName,
    type,
}: FactSheetOrKiidProps & { displayName: string }) => {
    return (
        <div className="my-2 flex items-center justify-start pl-4">
            <FactSheetOrKiid
                factSheetOrKiid={factSheetOrKiid}
                fileName={fileName}
                selectionActive={selectionActive}
                label={label}
                nullReplacementElem={<PdfIcon className="opacity-50" />}
                showLabel={false}
                showName={false}
                type={type}
            />
            <span className="ml-2 pt-1 text-sm">{displayName}</span>
        </div>
    );
};
